import { useState, useEffect, useMemo, useRef } from 'react'
import axios from 'axios'

import { usePrevious } from './useUserEmailTemplates'
import { useAdminURL } from '../contexts/Domain'

export interface Employee {
    EId: string
    email: string
    firstName: string
    fullName: string
    lastName: string
    userProfilePhotoUrl: string
}

interface EmployeeResponse {
    employees: Array<Employee>
    employeesCount: number
}

interface UseEmployeeAutocompleteProps {
    page: number
    keywords: string
    onNewResults?: (response: EmployeeResponse) => void
    onNextPageResults?: (response: EmployeeResponse) => void
    onError?: (response: any) => void
}
export default function useEmployeeAutocomplete(props: UseEmployeeAutocompleteProps) {
    const adminURL = useAdminURL()

    const cancelRef = useRef(null)
    let { current: cancelUpdates } = cancelRef
    const [employees, setEmployees] = useState([])
    const [employeesCount, setEmployeesCount] = useState(0)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    const fetchNewResults = (hasPageChanged?: boolean) => {
        setLoading(true)
        setError('')

        axios.get<EmployeeResponse>(`${adminURL}/action/user/search?&q=${props.keywords || ''}&action=getEmployees&pageSize=50&page=${props.page}&fetchMaxSize=true`)
            .then(response => {
                if (cancelUpdates) return

                setLoading(false)

                if (response.status === 200) {
                    setEmployees(response.data.employees)
                    setEmployeesCount(response.data.employeesCount)
                    if (hasPageChanged) {
                        if (typeof props.onNextPageResults === 'function') {
                            props.onNextPageResults(response.data)
                        }
                    } else {
                        if (typeof props.onNewResults === 'function') {
                            props.onNewResults(response.data)
                        }
                    }
                } else {
                    setError('An error occurred while retrieving results')
                }
            })
            .catch(err => {
                if (cancelUpdates) return

                setLoading(false)
                setError('An error occurred while retrieving results')
                console.error(err)
            })
    }

    const prevPage = usePrevious(props.page)

    useEffect(() => {
        cancelUpdates = false
        const hasPageChanged = props.page !== prevPage

        fetchNewResults(hasPageChanged)

        return () => {
            cancelUpdates = true
        }
    }, [props.page, props.keywords])

    return useMemo(() => ({
        employees,
        employeesCount,
        loading,
        error,
    }), [
        employees,
        employeesCount,
        loading,
        error,
    ])
}
